import { useRef, useEffect, forwardRef, useState } from 'react';
import styles from './TextAreaComponent.module.css';

const TextAreaComponent = forwardRef((props, ref) => {
    var { 
        onFocus, onBlur, focused = false, value, keyboardOpenAt, onChange, keyboardRef, placeHolder,
        styleType, fontSize, width, height, marginTop, marginBottom, marginLeft, onEnterClicked, isChat = false, rowsNumber = 1, maxRows = null
    } = props;

    const caretPosition = useRef();
    const currentKeyboard = useRef();
    const lastValue = useRef();
    const rowsNumberRef = useRef(rowsNumber ? rowsNumber : 1);
    const heightRef = useRef(height ? `${height}px` : "80px");
    const lineHeightRef = useRef(0);
    const maxHeightRef = useRef(null);
    const enterJustClicked = useRef(false)
    const componentInstance = useRef(null);

    const [selected, setSelected] = useState(false);
    const [keyboardClicked, setKeyboardClicked] = useState(false);

    useEffect(() => {
        ref.current.value = value;
        if(value === ""){
            autoGrow()
        }
    }, [value])


    useEffect(() => {
        componentInstance.current = ref.current;
        ref.current.value = value;
        lastValue.current = "";

        ref.current.keyboardOpenAt = keyboardOpenAt;
        ref.current.setKeyboardClicked = () => {
            setKeyboardClicked(true);
        };

        ref.current.enterClicked = () => {

            if (!caretPosition.current) {
                caretPosition.current = ref.current.selectionStart;
            }

            onEnterClicked?.();
        };

        ref.current.addToValue = (key) => {

            setKeyboardClicked(true);

            let start = ref.current.selectionStart;
            let end = ref.current.selectionEnd;

            const newValue = ref.current.value.slice(0, start) + key + ref.current.value.slice(end);
            ref.current.value = newValue;
            const newCursorPosition = start + key.length;
            ref.current.setSelectionRange(newCursorPosition, newCursorPosition);

            const inputEvent = new Event('input', { bubbles: true });
            ref.current.dispatchEvent(inputEvent);

            onTextChanged();

        };

        ref.current.removeOneFromValue = () => {
            setKeyboardClicked(true);

            let start = ref.current.selectionStart;
            let end = ref.current.selectionEnd;

            if (start === end) {
                if (start > 0) {
                    const newValue = ref.current.value.slice(0, start - 1) + ref.current.value.slice(end);
                    ref.current.value = newValue;
                    ref.current.setSelectionRange(start - 1, start - 1);
                }
            } else {
                const newValue = ref.current.value.slice(0, start) + ref.current.value.slice(end);
                ref.current.value = newValue;
                ref.current.setSelectionRange(start, start);
            }

            const inputEvent = new Event('input', { bubbles: true });
            ref.current.dispatchEvent(inputEvent);

            onTextChanged();
        };

        if (focused) {
            ref.current.focus();
        }

        currentKeyboard.current = keyboardRef.current;

        setTimeout(() => {
            keyboardRef.current?.addInputElement(ref.current);

        }, 100)

        autoGrow();

        return () => {
            currentKeyboard.current?.removeInputElement(componentInstance.current);
        }
    }, [])

    useEffect(() => {
        if (focused) {
            setTimeout(() => {
                ref.current.focus();
            }, 100)
        }
    }, [focused])

    const onTextChanged = () => {

        if (onChange) {
            if (lastValue.current !== ref.current.value) {
                onChange?.(ref.current?.value);
                lastValue.current = ref.current.value;
            }
        }

        autoGrow()
    }

    const getClassName = (styleType) => {
        switch (styleType) {
            case 'POPUP':
                return styles.inputPopup;
            case 'CHAT':
                return styles.chat;
            default:
                return styles.input;
        }
    };

    const onKeyUp = (e) => {
        if (e.key === 'Enter' && e.shiftKey) {
            console.log('Shift + Enter detected');
        } else if (e.key === 'Enter') {
            onEnterClicked?.();

            if(isChat){
                ref.current.value = '';
                setTimeout(() => {
                    autoGrow()
                }, 200);                
            }
        }
    }

    const autoGrow = () => {

        if(enterJustClicked.current){
            return;
        }

        ref.current.style.height = "auto";

        const scrollHeight = ref.current.scrollHeight;

        if (lineHeightRef.current === 0 && rowsNumberRef.current > 0) {
            const tempDiv = document.createElement("div");
            tempDiv.style.visibility = "hidden";
            tempDiv.style.position = "absolute";
            tempDiv.style.lineHeight = getComputedStyle(ref.current).lineHeight;
            tempDiv.style.fontSize = getComputedStyle(ref.current).fontSize;
            tempDiv.innerText = "A";
            document.body.appendChild(tempDiv);
            lineHeightRef.current = tempDiv.clientHeight;
            document.body.removeChild(tempDiv);
        }

        if (maxRows && lineHeightRef.current) {
            maxHeightRef.current = lineHeightRef.current * maxRows;
        }

        if (maxHeightRef.current && scrollHeight > maxHeightRef.current) {
            ref.current.style.height = `${maxHeightRef.current}px`;
            ref.current.style.overflowY = "auto";
        } else {
            ref.current.style.height = `${scrollHeight}px`;
            ref.current.style.overflowY = "hidden";
        }

        heightRef.current = ref.current.style.height;
    };
    
    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            enterJustClicked.current = true
            setTimeout(() => {
                enterJustClicked.current = false
            }, 200);
        }
    }

    return (
        <div style={{
            padding: "0px",
            width: width,
            position: "relative",
            marginTop: marginTop,
            marginBottom: marginBottom,
            display: "flex",
            justifyContent: "start",
            flexDirection: "column",
            marginLeft: marginLeft
        }}>

            <textarea ref={ref}
                step={0.1}
                rows={rowsNumber}
                className={getClassName(styleType)}
                placeholder={placeHolder}
                onKeyDown={onKeyDown}
                onKeyUp={(e) => onKeyUp(e)}
                onFocus={() => {
                    if (!keyboardClicked) {
                        if (keyboardRef.current)
                            keyboardRef.current.setInputElement(ref.current);
                        setSelected(true);
                    }
                    onFocus?.();
                    setKeyboardClicked(false);
                }}
                onBlur={() => {
                    caretPosition.current = ref.current.selectionStart;
                    if (!keyboardClicked) {
                        setSelected(false);
                        keyboardRef.current.setInputElement(null);
                        onBlur?.();
                    } else {
                        ref.current.focus();
                    }
                }}
                onChange={() => onTextChanged()}
                style={{
                    fontSize: fontSize ? fontSize : 40,
                    outline: 0,
                    border: selected ? "2px solid #33FFC8" : "0",
                    width: "100%",
                    boxSizing: "border-box",
                    paddingLeft: isChat ? "75px" : "40px",
                    paddingRight: isChat ? "80px" : "",
                    resize: "none"
                }}
            />
        </div>
    );
})

export default TextAreaComponent;

